import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";

export default function MyAttendanceSalary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [salaryInfo, setSalaryInfo] = useState(null);
  const [stats, setStats] = useState({
    presentDays: 0,
    absentDays: 0,
    lateDays: 0,
    totalDays: 0,
    currentMonthSalary: 0,
  });
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Check if we should show the attendance dialog
    // Don't show if it's a holiday or Sunday
    if (!loading && todayAttendance && !todayAttendance.marked && !todayAttendance.isHoliday) {
      const today = new Date().toISOString().split('T')[0];
      const currentDate = new Date();
      const isSunday = currentDate.getDay() === 0; // 0 = Sunday
      const lastShown = localStorage.getItem('attendanceDialogShown');
      
      if (lastShown !== today && !isSunday) {
        setShowAttendanceDialog(true);
        localStorage.setItem('attendanceDialogShown', today);
      }
    }
  }, [loading, todayAttendance]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch today's attendance status
      const todayResponse = await fetch(
        `${API_BASE_URL}/api/staff/my-attendance/today`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (todayResponse.ok) {
        const todayData = await todayResponse.json();
        setTodayAttendance(todayData);
      }

      // Fetch attendance records with time tracking support
      const recordsResponse = await fetch(
        `${API_BASE_URL}/api/staff/my-attendance/records-with-tracking`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (recordsResponse.ok) {
        const recordsData = await recordsResponse.json();
        setAttendanceRecords(recordsData.records || []);
        setStats(recordsData.stats || stats);
      }

      // Fetch salary information with staff type
      const salaryResponse = await fetch(
        `${API_BASE_URL}/api/staff/my-salary-with-type`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (salaryResponse.ok) {
        const salaryData = await salaryResponse.json();
        setSalaryInfo(salaryData);
      } else {
        console.error("Failed to fetch salary:", await salaryResponse.text());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttendanceClick = (status) => {
    setPendingStatus(status);
    setShowConfirmDialog(true);
  };

  const confirmMarkAttendance = async () => {
    try {
      setMarking(true);
      setShowConfirmDialog(false);
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE_URL}/api/staff/mark-attendance`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: pendingStatus, reachingTime: null }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(`Attendance marked as ${pendingStatus}!`);
        setShowAttendanceDialog(false);
        setPendingStatus(null);
        fetchData(); // Refresh data
      } else {
        toast.error(data.message || "Failed to mark attendance");
      }
    } catch (error) {
      console.error("Error marking attendance:", error);
      toast.error("Failed to mark attendance");
    } finally {
      setMarking(false);
    }
  };

  const handleDialogClose = () => {
    setShowAttendanceDialog(false);
  };

  const generateAllMonths = () => {
    const months = [];
    const currentDate = new Date();
    
    // If no salary info, only show current month
    if (!salaryInfo || !salaryInfo.joiningDate) {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const monthYear = currentDate.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
      
      const monthRecords = attendanceRecords.filter((record) => {
        const recordDate = new Date(record.date);
        return recordDate.getMonth() + 1 === month && recordDate.getFullYear() === year;
      });
      
      return [{
        monthYear,
        month,
        year,
        records: monthRecords,
      }];
    }
    
    // Start from joining month
    const startDate = new Date(salaryInfo.joiningDate);
    let iterDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    
    while (iterDate <= endDate) {
      const month = iterDate.getMonth() + 1; // 1-12
      const year = iterDate.getFullYear();
      const monthYear = iterDate.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
      
      // Get records for this month
      const monthRecords = attendanceRecords.filter((record) => {
        const recordDate = new Date(record.date);
        return recordDate.getMonth() + 1 === month && recordDate.getFullYear() === year;
      });
      
      months.push({
        monthYear,
        month,
        year,
        records: monthRecords,
      });
      
      // Move to next month
      iterDate.setMonth(iterDate.getMonth() + 1);
    }
    
    // Return in reverse order (most recent first)
    return months.reverse();
  };

  const handleViewMonth = (month, year) => {
    navigate(`/attendance-calendar/${month}/${year}`);
  };

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            bgcolor: "#1a1a1a",
          }}
        >
          <CircularProgress sx={{ color: "#C59A58" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 0.5, sm: 2, md: 4 }, minHeight: "calc(100vh - 80px)", overflow: 'hidden' }}>
        <Typography
          variant="h3"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 700,
            color: "#C4A574",
            textDecoration: "underline",
            textDecorationThickness: 2,
            textUnderlineOffset: 8,
            fontSize: { xs: "1.5rem", sm: "2rem", md: "3rem" }
          }}
        >
          MY ATTENDANCE & SALARY
        </Typography>

        {/* Salary and Attendance Records - Monthly View */}
        <Card sx={{ boxShadow: 3, bgcolor: "#2d2d2d" }}>
          <CardContent>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 2, color: "#C4A574", fontSize: { xs: "0.8rem", sm: "1.25rem", md: "1.5rem" } }}
            >
              Salary and Attendance Records
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,0.04)" }} />

            <Box>
              {generateAllMonths().length === 0 ? (
                <Typography align="center" sx={{ py: 4, color: "#9CA3AF" }}>
                  No months to display
                </Typography>
              ) : (
                generateAllMonths().map((data) => (
                  <Card
                    key={data.monthYear}
                    sx={{
                      mb: 2,
                      bgcolor: "#2d2d2d",
                      boxShadow: 1,
                      "&:hover": { boxShadow: 3, borderColor: "#C4A574" },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.8rem", sm: "1.25rem" } }}>
                            {data.monthYear}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF", fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                            {data.records.length} day{data.records.length !== 1 ? "s" : ""} recorded
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, flexWrap: "wrap" }}>
                          <Button
                            variant="contained"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewMonth(data.month, data.year)}
                            sx={{
                              bgcolor: "#C4A574",
                              color: "white",
                              "&:hover": { bgcolor: "#A27E4E" },
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                              px: { xs: 1, sm: 2 },
                              py: { xs: 0.5, sm: 1 },
                            }}
                          >
                            View Attendance
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<AttachMoneyIcon />}
                            onClick={() => navigate(`/my-salary-details/${data.month}/${data.year}`)}
                            sx={{
                              bgcolor: "#C4A574",
                              color: "white",
                              "&:hover": { bgcolor: "#A27E4E" },
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                              px: { xs: 1, sm: 2 },
                              py: { xs: 0.5, sm: 1 },
                            }}
                          >
                            View Salary
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Attendance Marking Dialog */}
        <Dialog
          open={showAttendanceDialog}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                bgcolor: "#2d2d2d",
              }
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: "#2b2b2b", color: "white", fontWeight: 600 }}>
            Mark Today's Attendance
          </DialogTitle>
          <DialogContent sx={{ mt: 3, bgcolor: "#4a4a4a" }}>
            <Typography variant="body1" sx={{ mb: 3, color: "#fff" }}>
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "#9CA3AF" }}>
              Please mark your attendance for today. Once marked, it cannot be edited.
            </Typography>
            
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => handleMarkAttendanceClick("Present")}
                disabled={marking}
                sx={{
                  bgcolor: "#4CAF50",
                  color: "white",
                  py: 1.5,
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#45A049" },
                }}
              >
                P - Present
              </Button>

              <Button
                variant="contained"
                size="large"
                onClick={() => handleMarkAttendanceClick("Absent")}
                disabled={marking}
                sx={{
                  bgcolor: "#F44336",
                  color: "white",
                  py: 1.5,
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#E53935" },
                }}
              >
                A - Absent
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#4a4a4a" }}>
            <Button onClick={handleDialogClose} sx={{ color: "#C4A574" }}>
              Cancel (Mark Later)
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          maxWidth="xs"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                bgcolor: "#2d2d2d",
              }
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: "#2b2b2b", color: "white", fontWeight: 600 }}>
            Confirm Attendance
          </DialogTitle>
          <DialogContent sx={{ mt: 3, bgcolor: "#4a4a4a" }}>
            <Typography variant="body1" sx={{ color: "#fff" }}>
              Are you sure you want to mark your attendance as <strong>{pendingStatus}</strong>?
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: "#9CA3AF" }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#4a4a4a" }}>
            <Button 
              onClick={() => setShowConfirmDialog(false)} 
              sx={{ color: "#9CA3AF" }}
              disabled={marking}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmMarkAttendance}
              variant="contained"
              disabled={marking}
              sx={{
                bgcolor: "#C4A574",
                color: "white",
                "&:hover": { bgcolor: "#A27E4E" },
              }}
            >
              {marking ? "Marking..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppLayout>
  );
}
